import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  isRouteErrorResponse,
} from "react-router";
import type { LinksFunction } from "react-router";
import { ClerkProvider } from "@clerk/clerk-react";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { SITE_NAME } from "@/lib/constants";
import "./index.css";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=JetBrains+Mono:wght@400;500&display=swap",
  },
  { rel: "icon", href: "/logo.svg", type: "image/svg+xml" },
  { rel: "alternate", href: "/llms.txt", type: "text/plain", title: "LLM API Reference" },
  { rel: "alternate", href: "/llms-full.txt", type: "text/plain", title: "Complete API Reference" },
  { rel: "alternate", href: "/docs.md", type: "text/markdown", title: "API Documentation (Markdown)" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <noscript>
          <div style={{ maxWidth: 700, margin: "2rem auto", padding: "0 1rem", fontFamily: "system-ui,sans-serif", lineHeight: 1.6 }}>
            <h1>{SITE_NAME} — Logo API for Modern Developers</h1>
            <p>The free logo API. Fetch logos for any company, stock ticker, or cryptocurrency with a single image URL. No SDK required.</p>
            <ul>
              <li><a href="/llms.txt">llms.txt</a> — Short API reference</li>
              <li><a href="/llms-full.txt">llms-full.txt</a> — Full API reference</li>
              <li><a href="/docs.md">docs.md</a> — Documentation (markdown)</li>
              <li><a href="/openapi.json">openapi.json</a> — OpenAPI 3.0 spec</li>
            </ul>
          </div>
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const CLERK_KEY = "pk_test_c3BsZW5kaWQtcHJpbWF0ZS01NS5jbGVyay5hY2NvdW50cy5kZXYk";

export default function Root() {
  const location = useLocation();
  const hideChrome =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/dashboard");

  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
      <div className="min-h-screen flex flex-col">
        {!hideChrome && <Navbar />}
        <main className="flex-1">
          <Outlet />
        </main>
        {!hideChrome && <Footer />}
      </div>
    </ClerkProvider>
  );
}

export function ErrorBoundary({ error }: { error: unknown }) {
  if (isRouteErrorResponse(error)) {
    return (
      <div className="py-32 text-center">
        <h1 className="text-4xl font-bold mb-4">{error.status}</h1>
        <p className="text-muted-foreground">{error.statusText || "Page not found"}</p>
      </div>
    );
  }
  return (
    <div className="py-32 text-center">
      <h1 className="text-4xl font-bold mb-4">Something went wrong</h1>
      <p className="text-muted-foreground">Please try again later.</p>
    </div>
  );
}
